// src/data/editions/aviation.js
//
// AVIATION edition — regional-airline / operator skin (CommuteAir, United
// Express ERJ-145, bases at IAH + IAD). Unlike the insurer, an operator does
// not experience value as claims avoided; it experiences it as crews kept
// fit-to-fly. So this edition leads with the OPERATIONAL chain
//   fatigue -> unplanned absence -> reserve gap -> crew-related cancellation
// and demotes STD/LTD to the long tail of the same fatigue. Completion factor
// and on-time are the money under a capacity-purchase agreement.
//
// Brand palette is illustrative blue + yellow; drop in CommuteAir's exact hex
// once pulled from the site.

const aviation = {
    id: 'aviation',
    label: 'Aviation (Operator)',

    palette: { deep: '#0A2A5E', blue: '#1F6FB2', yellow: '#F6BE00' },

    // Console header titles per tab (EnterpriseConsole.getPageTitle).
    pageTitles: {
        'cc-overview': 'Crew Readiness Overview',
        'cc-triangulation': 'Triangulation: Crews, the Operator, the Average',
        'cc-data': 'Data Intake: The Operator’s Own Figures',
        'cc-ai': 'AI Intelligence & Charting',
        'cc-readiness': 'Readiness & Strain',
        'cc-predictive': 'Crew Availability Forecast',
        'cc-roi': 'Operational ROI & Availability',
        'cc-feedback': 'Anonymous Pulse & Sentiment',
        default: 'Crew Readiness Command Center'
    },

    // Legend label for the second series on the Overview trend chart.
    trendSignalLabel: 'Absence signal',

    summary: {
        source: 'demo', // drives the console's "Modeled example" badge (T0.3)
        capacityScore: 82,
        capacityTrend: 3,
        workforce: 1200,
        highSignalCohort: 142,
        topPriorityGroup: 'Flight Crew',
        topPulseTheme: 'Reserve & scheduling load',
        participationRate: 64,
        modeledClaimsAvoidance: '$2.1M',
        modeledStdClaims: { low: 20, high: 32, windowDays: 90 },
        modeledLtdTransition: { low: 6, high: 10 },
        leaveProbability: { mid: 18, low: 14, high: 22 },
        // Overview roll-up card copy (2nd + 3rd cards).
        outlookTitle: 'Claims Outlook',
        outlookNoun: 'STD claims',
        outlookUnit: 'claims',
        financialNoun: 'claims-avoidance'
    },

    capacityTrend: [
        { date: '1st', capacity: 79, signal: 22 },
        { date: '4th', capacity: 80, signal: 21 },
        { date: '8th', capacity: 78, signal: 24 },
        { date: '12th', capacity: 81, signal: 20 },
        { date: '16th', capacity: 80, signal: 21 },
        { date: '20th', capacity: 82, signal: 18 },
        { date: '24th', capacity: 81, signal: 19 },
        { date: '28th', capacity: 82, signal: 18 },
        { date: 'Today', capacity: 82, signal: 18 }
    ],

    readiness: {
        scoreLabel: 'Crew Readiness Score',
        signalSubtext: 'duty-period load + scheduled block hours',
        groups: [
            { name: 'Flight Crew', pressure: 79, note: 'Sustained duty-time & reserve load', responses: 96 },
            { name: 'Ground Operations', pressure: 76, note: 'Peak-bank turn pressure at IAH', responses: 84 },
            { name: 'Cabin Crew', pressure: 73, note: 'Elevated trip pairings', responses: 58 },
            { name: 'Aircraft Maintenance', pressure: 70, note: 'Overnight line-maintenance hours', responses: 31 },
            { name: 'Customer Service', pressure: 67, note: 'Within normal range', responses: 22 },
            { name: 'Flight Dispatch / OCC', pressure: 63, note: 'Within normal range', responses: 4 } // below floor
        ],
        signals: [
            { label: 'Self-reported fatigue trend', value: '+5%', tier: 'Intermediate', tone: 'amber' },
            { label: 'Unplanned absence & sick calls', value: '+7%', tier: 'Lagging', tone: 'amber' },
            { label: 'Reserve call-outs / reassignments', value: '+4%', tier: 'Lagging', tone: 'amber' },
            { label: 'Crew-leader check-in signal', value: 'Steady', tier: 'Intermediate', tone: 'emerald' }
        ]
    },

    feedback: {
        scopes: [
            { id: 'org', label: 'Organization-wide', responses: 312 },
            { id: 'flightcrew', label: 'Flight Crew', responses: 96 },
            { id: 'groundops', label: 'Ground Operations', responses: 84 },
            { id: 'cabincrew', label: 'Cabin Crew', responses: 58 },
            { id: 'dispatch', label: 'Flight Dispatch / OCC', responses: 4 } // below floor -> suppressed
        ],
        submissions: [
            { id: 1, time: '2 hours ago', theme: 'Fatigue', sentiment: 'risk', scope: 'flightcrew',
              text: 'Three back-of-clock reserve days in a row. By the last leg I am running on fumes - that is a safety issue, not a comfort one.' },
            { id: 2, time: '5 hours ago', theme: 'Tools', sentiment: 'positive', scope: 'cabincrew',
              text: 'The fatigue check-in actually got me swapped off a pairing before I had to call in sick. First time it felt like the company acted on it.' },
            { id: 3, time: 'Yesterday', theme: 'Culture', sentiment: 'positive', scope: 'groundops',
              text: 'Communication around the IAH base reassignments was much clearer this month. Knowing the plan early makes a real difference.' },
            { id: 4, time: 'Yesterday', theme: 'Workload', sentiment: 'risk', scope: 'groundops',
              text: 'The ramp is short on the evening bank and we are eating the delays. We need more people on the line at IAH.' },
            { id: 5, time: '2 days ago', theme: 'Recognition', sentiment: 'positive', scope: 'flightcrew',
              text: 'My crew lead flagged my fatigue before I had to bring it up. Felt genuinely looked after.' }
        ],
        sentiment: {
            positiveSummary: 'Strong response to the new fatigue check-in and clearer base-reassignment communication.',
            emergingTheme: 'Back-of-clock reserve assignments and short-staffed ground banks are the most frequent sources of fatigue.'
        }
    },

    predictive: {
        title: 'The Crew Availability Funnel',
        intro: 'Sustained fatigue in the high-fatigue-risk crew cohort is a leading driver of unplanned absences and reserve call-outs. When reserves are exhausted, those surface as crew-related cancellations - the operational cost. STD/LTD claims are the long tail of the same fatigue, not the headline. This is directional from leading signals - not a forecast - and needs a 6-12 month window and a comparison baseline before any causal claim.',
        lever: 'Earlier fatigue intervention',
        ownLabel: 'Absence & reserve-call-out rate',
        tiers: [
            { value: '1,200', label: 'Total crew & staff', source: 'observed', tone: 'neutral', width: 100 },
            { value: '142', label: 'High-fatigue-risk crew cohort', source: 'observed', tone: 'brand', width: 90 },
            { value: '55-80', label: 'Modeled unplanned absences / fatigue calls - qtr', source: 'modeled', tone: 'amber', width: 76 },
            { value: '6-12', label: 'Reserve gaps -> crew-related cancellations', source: 'modeled', tone: 'amber', width: 60 },
            { value: '6-10', label: 'STD -> LTD transitions - health tail', source: 'modeled', tone: 'muted', width: 46 }
        ],
        cards: [
            { title: 'Fatigue-Absence Association', value: '~4.2x', valueTone: 'amber', level: 'Intermediate', source: 'modeled', dark: false,
              subtext: 'Modeled association - not causal',
              body: 'Crew groups sustaining low readiness and high fatigue show a higher rate of unplanned absence and reserve call-outs. In comparable modeled populations the association is roughly 4.2x - directional, and confirmed only against an operator baseline.' },
            { title: 'Modeled Reserve-Gap Risk', value: '18%', valueTone: 'white', level: 'Lagging', source: 'modeled', dark: true,
              subtext: 'Midpoint - 14-22% band',
              body: 'Modeled probability that sustained fatigue in a high-load crew group converts to a reserve-coverage gap this quarter. The band widens or narrows as operator baseline data is added.' },
            { title: 'Proactive Utilization Lift', value: '+34%', valueTone: 'emerald', level: 'Leading', source: 'observed', dark: false,
              subtext: 'Observed in engaged cohorts - attribution pending',
              body: 'Crew engaged with AI Coach recommendations route earlier into existing EAP / peer-support and paramedical benefits - a leading indicator, shown as an association rather than a causal result.' }
        ],
        footer: {
            lead: 'These are models, not forecasts.',
            note: 'Unplanned absence and reserve call-outs are the leading, attributable metrics EAS owns. Reserve gaps, crew-related cancellations and any STD/LTD transition are lagging outcomes that require a longer window and a comparison baseline before any causal claim. Cancellations have many causes (weather, ATC, maintenance); only the crew-availability contribution is modeled.'
        }
    },

    roi: {
        currency: 'USD',
        locale: 'en-US',
        benchSource: 'sector reference',
        // Blended replacement cost runs higher in aviation; pilots/AMTs higher still.
        replacementMultiple: 0.75,
        turnoverRate: 16,
        termLabels: {
            lostTime: 'Protected Crew Availability',
            lostTimeBasis: 'Avoided crew-absence days x loaded daily cost x modeled reduction. Daily cost is the conservative floor - reserve-coverage and cancellation costs run higher and are not booked here.'
        }
    },

    copilot: { exampleScope: 'IAH Ground Operations' }
};

export default aviation;
